import { AnimatePresence, motion } from 'framer-motion';
import Fuse from 'fuse.js';
import React, {
	type Dispatch,
	type RefObject,
	type SetStateAction,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Sheet, useVirtualKeyboard } from 'react-modal-sheet';
import { jost, notoSans } from '@/pages/_app';
import { useTranslationContext } from '@/context/translation';
import { useDebounce, useTranslationWithLocale } from '@/hooks';
import { AmplitudeService } from '@/services';
import {
	type LandingItem,
	type LandingTool,
	type LandingTools,
} from '@/data/aiToolLandings';
import { AMPLITUDE_EVENTS } from '@/constants';
import { nothingFoundVariants, searchResultsVariants } from '@/animations';
import CloseIcon from '@/public/icons/cross.svg';
import styles from './Search.module.scss';

interface SearchMobileProps {
	data: LandingTools['hero'];
	searchActive: boolean;
	setActiveTabId: Dispatch<SetStateAction<string>>;
	setSearchActive: Dispatch<SetStateAction<boolean>>;
	setActivePositioningId: Dispatch<SetStateAction<string | null>>;
	setActiveFeature: Dispatch<SetStateAction<LandingTool | null>>;
	setPinnedFeatureId: Dispatch<SetStateAction<string | null>>;
	toolsContainer: RefObject<HTMLDivElement>;
}

const PADDING = 32;

export const SearchMobile = ({
	data,
	searchActive,
	setActiveTabId,
	setActivePositioningId,
	setActiveFeature,
	setPinnedFeatureId,
	setSearchActive,
	toolsContainer,
}: SearchMobileProps) => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'landing-config',
		locale,
		translations?.['landing-config'] as Record<string, unknown> | undefined,
	);
	const { t: tTools } = useTranslationWithLocale(
		'tools',
		locale,
		translations?.['tools'] as Record<string, unknown> | undefined,
	);

	const inputRef = useRef<HTMLInputElement>(null);
	const [searchQuery, setSearchQuery] = useState('');

	// Creates a new memoized array of landings for Fuse search, extending every landing item with a 'tags' key.
	// The 'tags' key consists of category tags + group title + item title.
	const landingArray = useMemo(() => {
		return Object.values(data.toolsListing).flatMap(category => {
			const categoryTags = category.tags.map(tag => tag.toLowerCase());
			return category.tools.flatMap(toolGroup => {
				const groupTitle = toolGroup.titleEng.toLowerCase();
				return toolGroup.list.map(item => ({
					...item,
					tags: [...categoryTags, groupTitle, item.titleEng.toLowerCase()],
				}));
			});
		});
	}, [data.toolsListing]);

	const fuse = useMemo(
		() =>
			new Fuse(landingArray, {
				keys: [
					{ name: 'titleEng', weight: 0.9 },
					{ name: 'tags', weight: 0.1 },
				],
				includeScore: true,
				threshold: 0.25,
				minMatchCharLength: 2,
			}),
		[landingArray],
	);

	const queryLengthValid = searchQuery.trim().toLowerCase().length >= 2;

	const fuseResult = queryLengthValid
		? fuse.search(searchQuery.trim().toLowerCase())
		: [];

	const resultsToRender = fuseResult.map(r => r.item);

	const closeSearch = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_CLOSED);
		setSearchQuery('');
		setSearchActive(false);
	};

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	const handleSearchResultClick = (landingItem: LandingItem) => {
		let foundFeature: LandingTool | null = null;

		Object.values(data.toolsListing).forEach(category => {
			const feature = category.tools.find(toolGroup =>
				toolGroup.list.some(item => item.id === landingItem.id),
			);
			if (feature) foundFeature = feature;
		});

		if (!foundFeature) return;

		AmplitudeService.logEvent(
			AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_RESULT_PRESSED,
			{
				feature: (foundFeature as LandingTool).titleEng,
			},
		);

		setActiveTabId(landingItem.category);
		setActivePositioningId(landingItem.id);
		setActiveFeature(foundFeature as LandingTool);
		setPinnedFeatureId((foundFeature as LandingTool).id);
		closeSearch();
		setTimeout(() => scrollIntoViewIfNeeded(landingItem), 250);
	};

	const scrollIntoViewIfNeeded = (landingItem: LandingItem) => {
		if (!toolsContainer.current) return;

		const activeFeature = Object.values(data.toolsListing).find(block =>
			block.tools
				.flatMap(item => item.list)
				.some(item => item.id === landingItem.id),
		);

		if (!activeFeature) return;

		const element = toolsContainer.current?.querySelector(
			`div[data-title='${t(activeFeature.title)}']`,
		);
		const activeRow = element as HTMLDivElement | null;

		if (!activeRow) return;

		window?.scrollTo({
			top: activeRow.offsetTop - PADDING,
			behavior: 'smooth',
		});
	};

	// Debounced analytics
	const debouncedLog = useDebounce(() => {
		if (!queryLengthValid) return;
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_USED, {
			result: resultsToRender.length > 0 ? 'success' : 'none',
		});
	}, 500);

	useEffect(() => {
		debouncedLog();
	}, [searchQuery]);

	const { keyboardHeight } = useVirtualKeyboard({
		isEnabled: searchActive,
	});

	return (
		<Sheet
			isOpen={searchActive}
			onClose={closeSearch}
			className={styles['search-mobile']}
			containerClassName={`${notoSans.variable} ${jost.variable}`}
			onOpenEnd={() => inputRef.current?.focus()}
		>
			<Sheet.Container className={styles['search-mobile__container']}>
				<Sheet.Content disableDrag className={styles['search-mobile__content']}>
					<div className={styles['search-mobile__inner']}>
						<div className={styles['search-mobile__field']}>
							<span className={styles['search-mobile__field_icon']}>🔎</span>

							<input
								id='search-input'
								ref={inputRef}
								type='text'
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								placeholder={tTools('heroData_searchInputPlaceholder')}
								className={styles['search-mobile__field_input']}
								autoComplete='off'
								autoCorrect='off'
								autoCapitalize='off'
								// eslint-disable-next-line jsx-a11y/no-autofocus
								autoFocus
							/>

							{searchQuery.trim() && (
								<button
									type='button'
									className={styles['search-mobile__field_close']}
									onClick={() => setSearchQuery('')}
								>
									<CloseIcon />
								</button>
							)}
						</div>

						<div className={styles['search-mobile__scroll']}>
							<AnimatePresence mode='wait'>
								{queryLengthValid && resultsToRender.length > 0 && (
									<motion.div
										key='results'
										variants={searchResultsVariants}
										initial='initial'
										animate='animate'
										exit='exit'
										transition={{ duration: 0.15, ease: 'easeOut' }}
										className={styles['search-mobile__results']}
									>
										<div className={styles['search-mobile__results_content']}>
											{resultsToRender.map(landingItem => (
												<button
													key={landingItem.id}
													type='button'
													className={styles['search-mobile__results_item']}
													onClick={() => handleSearchResultClick(landingItem)}
												>
													<p
														className={
															styles['search-mobile__results_item_inner']
														}
													>
														{t(landingItem.title)}
													</p>
												</button>
											))}
										</div>
									</motion.div>
								)}

								{queryLengthValid && resultsToRender.length === 0 && (
									<motion.div
										key='empty'
										variants={nothingFoundVariants}
										initial='initial'
										animate='animate'
										exit='exit'
										className={styles['search-mobile__empty']}
										transition={{ duration: 0.18, ease: 'easeOut' }}
									>
										<div className={styles['search-mobile__empty_inner']}>
											<p>{tTools('heroData_nothingFound1')}🤔</p>
											<p>{tTools('heroData_nothingFound2')}</p>
										</div>
									</motion.div>
								)}
							</AnimatePresence>

							<div
								className={styles['search-mobile__footer']}
								style={{ bottom: keyboardHeight }}
							>
								<button
									type='button'
									className={styles['search-results__close']}
									onClick={closeSearch}
									aria-label='Close search'
								>
									Close <CloseIcon />
								</button>
							</div>
						</div>
					</div>
				</Sheet.Content>
			</Sheet.Container>
			<Sheet.Backdrop onClick={(e: any) => e.stopPropagation()} />
		</Sheet>
	);
};
